import React, { useState, useEffect, useCallback, startTransition } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface WSCNavigationProps {
  containerRef: React.RefObject<HTMLDivElement | null>
  isLoaded: boolean
  isEmpty: boolean
  isPlaceholder?: boolean
}

interface ButtonProps {
  icon: IconProp
  ariaLabel: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
}

export const WSCNavigation: React.FC<WSCNavigationProps> = ({
  containerRef,
  isLoaded,
  isEmpty,
  isPlaceholder
}) => {
  const [scrollState, setScrollState] = useState({
    isAtStart: true,
    isAtEnd: false
  })

  const updateScrollState = useCallback(() => {
    if (!containerRef.current) return

    const carouselElement = containerRef.current.querySelector('[id^="wsc-container-"]')?.firstChild as HTMLElement
    if (!carouselElement) return

    const isAtStart = carouselElement.scrollLeft <= 0
    const isAtEnd = carouselElement.scrollLeft + carouselElement.clientWidth >= carouselElement.scrollWidth - 1

    setScrollState({ isAtStart, isAtEnd })
  }, [containerRef])

  useEffect(() => {
    if (!containerRef.current || !isLoaded) return

    const carouselElement = containerRef.current.querySelector('[id^="wsc-container-"]')?.firstChild as HTMLElement
    if (!carouselElement) return

    // Initial state check (deferred to avoid synchronous setState in effect)
    startTransition(() => updateScrollState())

    // Listen to scroll events
    carouselElement.addEventListener('scroll', updateScrollState)

    // Listen to resize events to update state when window size changes
    window.addEventListener('resize', updateScrollState)

    return () => {
      carouselElement.removeEventListener('scroll', updateScrollState)
      window.removeEventListener('resize', updateScrollState)
    }
  }, [containerRef, isLoaded, updateScrollState])

  const handlePrev = () => {
    if (!containerRef.current) return
    const carouselElement = containerRef.current.querySelector('[id^="wsc-container-"]')?.firstChild as HTMLElement
    if (carouselElement) {
      const scrollAmount = carouselElement.offsetWidth * 0.8
      carouselElement.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  const handleNext = () => {
    if (!containerRef.current) return
    const carouselElement = containerRef.current.querySelector('[id^="wsc-container-"]')?.firstChild as HTMLElement
    if (carouselElement) {
      const scrollAmount = carouselElement.offsetWidth * 0.8
      carouselElement.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className={classes.container}>
      <NavigationButton
        icon={faChevronLeft}
        ariaLabel="Previous slides"
        onClick={handlePrev}
        disabled={scrollState.isAtStart || isEmpty || !isLoaded}
        isLoading={isPlaceholder || !isLoaded}
      />
      <NavigationButton
        icon={faChevronRight}
        ariaLabel="Next slides"
        onClick={handleNext}
        disabled={scrollState.isAtEnd || isEmpty || !isLoaded}
        isLoading={isPlaceholder || !isLoaded}
      />
    </div>
  )
}

const NavigationButton: React.FC<ButtonProps> = (props) => {
  const { icon, ariaLabel, onClick, disabled = false, isLoading } = props
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={classNames({
        [classes.button]: true,
        [classes.button__disabled]: disabled,
        [classes.button__loading]: isLoading,
      })}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  )
}
